import type {
  CouponDurationResource,
  CouponResource,
  CouponStatusResource,
  CouponTypeResource,
  MaxOffResource,
  OffTypeResource,
} from '@/features/billing/api/resources/coupon'
import type {
  CouponDuration,
  CouponProps,
  CouponType,
  MaxOff,
  OffType,
} from '@/features/billing/coupons/Coupon'
import type { CouponListStatus } from '@/features/billing/coupons/CouponStore'
import { maybeParseDate, unixEpoch } from '@/utils/date-util'

/**
 * Maps a coupon.
 */
export function mapCouponResourceToProps(
  resource: CouponResource,
): CouponProps {
  return {
    id: resource.id,
    code: resource.code,
    workspaceId: resource.workspace_id,
    friendlyName: resource.friendly_name ?? '',
    description: resource.description ?? '',
    maxTotalRedemptions: resource.max_total_redemptions,
    dateExpires: maybeParseDate(resource.date_expires) ?? null,
    duration: mapCouponDurationResourceToCouponDuration(resource.duration),
    couponType: mapCouponTypeResourceToCouponType(resource.coupon_type),
    dateCreated: maybeParseDate(resource.date_created) ?? unixEpoch(),
    dateModified: maybeParseDate(resource.date_modified) ?? unixEpoch(),
    offType: mapOffTypeResourceToOffType(resource.off_type),
    maxOff: resource.max_off
      ? mapMaxOffResourceToMaxOff(resource.max_off)
      : null,
  }
}

/**
 * Maps a coupon type to its API value.
 */
export function mapCouponTypeToResource(
  type: CouponType['type'],
): CouponTypeResource['type'] {
  switch (type) {
    case 'Regular':
      return 'REGULAR'
    case 'ReferralCode':
      return 'REFERRAL_CODE'
    case 'CustomDiscount':
      return 'CUSTOM_DISCOUNT'
  }
}

function mapCouponDurationResourceToCouponDuration(
  resource: CouponDurationResource,
): CouponDuration {
  switch (resource) {
    case 'ONCE':
      return 'Once'
    case 'FOREVER':
      return 'Forever'
  }
}

function mapCouponTypeResourceToCouponType(
  resource: CouponTypeResource,
): CouponType {
  switch (resource.type) {
    case 'REGULAR':
      return { type: 'Regular' }
    case 'REFERRAL_CODE':
      return {
        type: 'ReferralCode',
        referrerUserId: resource.referrer_user_id,
      }
    case 'CUSTOM_DISCOUNT':
      return {
        type: 'CustomDiscount',
        createdByUserId: resource.created_by_user_id,
      }
  }
}

function mapOffTypeResourceToOffType(resource: OffTypeResource): OffType {
  switch (resource.type) {
    case 'AMOUNT_OFF':
      return { type: 'AmountOff', amountOff: resource.amount_off }
    case 'PERCENT_OFF':
      return { type: 'PercentOff', percentOff: resource.percent_off }
  }
}

function mapMaxOffResourceToMaxOff(resource: MaxOffResource): MaxOff {
  switch (resource.type) {
    case 'MAX_AMOUNT_OFF':
      return { type: 'MaxAmountOff', amountOff: resource.amount_off }
    case 'MAX_PERCENT_OFF':
      return { type: 'MaxPercentOff', percentOff: resource.percent_off }
  }
}

/**
 * Maps the status to list coupons by to its API value.
 */
export function mapCouponListStatusToResource(
  status: CouponListStatus,
): CouponStatusResource {
  switch (status) {
    case 'Active':
      return 'ACTIVE'
    case 'Expired':
      return 'EXPIRED'
    case 'All':
      return 'ALL'
    case 'Redeemed':
      return 'REDEEMED'
  }
}
